/**
 * PlotexMeasureTool — click-to-measure interaction for the Plotex viewer.
 *
 * Standard arch-viz affordance: user clicks a toolbar button, then clicks two
 * points on geometry to see the distance between them. Each completed
 * measurement persists as a sphere-line-sphere trio in the scene plus a small
 * HTML label near the midpoint. The HTML label is positioned by projecting
 * the midpoint through the camera each frame — the caller must invoke
 * `update()` from the engine's animate loop.
 *
 * Why HTML overlay instead of a CSS2DRenderer / sprite: a plain absolutely
 * positioned <div> avoids pulling in an extra renderer and composes cleanly
 * with the existing post-processing chain. The tradeoff is one
 * camera.project() per label per frame, which is cheap (< 1 microsecond each).
 *
 * Why depthTest=false + renderOrder=999 on markers and lines: the measurement
 * overlay is a UI affordance, not world content. Users expect to see their
 * measurement markers even when they punched through a wall — same behavior
 * as SketchUp, Rhino, Revit. We render them last and without depth test so
 * they always sit on top.
 */

import {
  BufferAttribute,
  BufferGeometry,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  PerspectiveCamera,
  Raycaster,
  Scene,
  SphereGeometry,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';
import type { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export interface PlotexMeasurement {
  p1: Vector3;
  p2: Vector3;
  distance: number;
}

export interface PlotexMeasureToolOptions {
  scene: Scene;
  camera: PerspectiveCamera;
  renderer: WebGLRenderer;
  /**
   * OrbitControls instance. When provided, the tool briefly disables the
   * controls during an active click so an orbit-drag-ending-over-geometry
   * doesn't accidentally place a point. The controls stay ENABLED outside
   * clicks so the user can freely reframe between measurements.
   */
  controls?: OrbitControls;
  /**
   * Root object to raycast against. Defaults to the full scene. Passing the
   * loaded glTF scene (not the entire three scene) avoids hitting helpers,
   * impostor quads, and measurement markers themselves.
   */
  targetMeshes?: Object3D;
}

type State = 'idle' | 'waitingForSecond';

/**
 * Per-measurement record kept alive until the user calls `clear()` or
 * `dispose()`. Holds both the three.js objects (so we can remove them from
 * the scene) and the HTML label (so we can reposition + remove it).
 */
interface Record {
  p1: Vector3;
  p2: Vector3;
  distance: number;
  marker1: Mesh;
  marker2: Mesh;
  line: LineSegments;
  labelEl: HTMLDivElement;
}

// Colors pulled out so both preview + completed measurements stay consistent.
const MARKER_COLOR = 0xff3366;
const LINE_COLOR = 0x4a9eff;
const MARKER_RADIUS = 0.2;
// Far clamp for the preview line when the ray misses geometry — gives users
// visual feedback that the tool is alive without anchoring at infinity.
const NO_HIT_CLAMP_METERS = 50;

export class PlotexMeasureTool {
  private scene: Scene;
  private camera: PerspectiveCamera;
  private renderer: WebGLRenderer;
  private controls?: OrbitControls;
  private targetMeshes: Object3D;

  private state: State = 'idle';
  private active = false;

  // First-click point (world space) while in `waitingForSecond`. Null in idle.
  private pendingP1: Vector3 | null = null;
  // Preview objects we show between the first click and the second click. Kept
  // around across measurements — we just toggle .visible — to avoid the GC
  // churn of rebuilding geometry on every mouse move.
  private previewMarker: Mesh;
  private previewLine: LineSegments;
  private previewLabel: HTMLDivElement;

  private records: Record[] = [];

  // Reusable scratch objects — allocating Vector3 / Vector2 inside pointer
  // handlers would churn the GC on every mouse move.
  private raycaster = new Raycaster();
  private pointerNdc = new Vector2();
  private midpointScratch = new Vector3();
  private ndcScratch = new Vector3();

  // Toolbar button + HTML container for the labels. Both are owned by the
  // tool so dispose() can clean up exactly what it created.
  private button: HTMLButtonElement;
  private labelRoot: HTMLDivElement;

  // Bound handlers we keep references to so removeEventListener() works.
  private onPointerDownBound: (e: PointerEvent) => void;
  private onPointerMoveBound: (e: PointerEvent) => void;
  private onKeyDownBound: (e: KeyboardEvent) => void;

  constructor(opts: PlotexMeasureToolOptions) {
    this.scene = opts.scene;
    this.camera = opts.camera;
    this.renderer = opts.renderer;
    this.controls = opts.controls;
    this.targetMeshes = opts.targetMeshes ?? opts.scene;

    // Build the preview marker + line + label once and park them in the
    // scene graph hidden. Flipping .visible is an O(1) operation; rebuilding
    // geometry on pointer move would be wasteful at 60 Hz.
    this.previewMarker = this._buildMarker();
    this.previewMarker.visible = false;
    this.scene.add(this.previewMarker);

    this.previewLine = this._buildLine();
    this.previewLine.visible = false;
    this.scene.add(this.previewLine);

    // Labels live in a single container layered over the canvas. The
    // container stays pointer-events-none so it never steals clicks from
    // OrbitControls or the measurement canvas itself — individual children
    // can opt back in if needed, but the measurement labels don't.
    this.labelRoot = document.createElement('div');
    Object.assign(this.labelRoot.style, {
      position: 'fixed',
      inset: '0',
      pointerEvents: 'none',
      zIndex: '9998',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    });
    document.body.appendChild(this.labelRoot);

    this.previewLabel = this._buildLabel();
    this.previewLabel.style.display = 'none';
    this.labelRoot.appendChild(this.previewLabel);

    // Toolbar button. Placed above the quality slider (which sits at
    // bottom:16px) so the two widgets stack without overlapping.
    this.button = this._buildToolbarButton();
    document.body.appendChild(this.button);

    // Bind handlers so removeEventListener can untangle them. Arrow
    // functions allocated here each keep a stable identity.
    this.onPointerDownBound = (e) => this._onPointerDown(e);
    this.onPointerMoveBound = (e) => this._onPointerMove(e);
    this.onKeyDownBound = (e) => this._onKeyDown(e);
  }

  // -- Public API -----------------------------------------------------------

  activate(): void {
    if (this.active) return;
    this.active = true;
    this.state = 'idle';
    this.pendingP1 = null;

    // Crosshair on the canvas ONLY — don't stomp on the body cursor because
    // the toolbar button lives there and should remain pointer-y.
    this.renderer.domElement.style.cursor = 'crosshair';

    this.renderer.domElement.addEventListener('pointerdown', this.onPointerDownBound);
    this.renderer.domElement.addEventListener('pointermove', this.onPointerMoveBound);
    window.addEventListener('keydown', this.onKeyDownBound);

    this._setButtonActive(true);
  }

  deactivate(): void {
    if (!this.active) return;
    this.active = false;
    this.state = 'idle';
    this.pendingP1 = null;

    this.renderer.domElement.style.cursor = '';

    this.renderer.domElement.removeEventListener('pointerdown', this.onPointerDownBound);
    this.renderer.domElement.removeEventListener('pointermove', this.onPointerMoveBound);
    window.removeEventListener('keydown', this.onKeyDownBound);

    this._hidePreview();
    this._setButtonActive(false);
  }

  /**
   * Remove all persistent measurements but keep the tool active. Mirrors the
   * "Delete all dimensions" affordance common in CAD tools.
   */
  clear(): void {
    for (const r of this.records) {
      this._disposeRecord(r);
    }
    this.records = [];
    // Also cancel an in-flight measurement so we don't strand a pendingP1.
    this.pendingP1 = null;
    this.state = 'idle';
    this._hidePreview();
  }

  dispose(): void {
    this.deactivate();
    this.clear();

    // Preview objects
    this.scene.remove(this.previewMarker);
    (this.previewMarker.geometry as BufferGeometry).dispose();
    (this.previewMarker.material as MeshBasicMaterial).dispose();
    this.scene.remove(this.previewLine);
    (this.previewLine.geometry as BufferGeometry).dispose();
    (this.previewLine.material as LineBasicMaterial).dispose();

    // DOM
    this.labelRoot.remove();
    this.button.remove();
  }

  isActive(): boolean {
    return this.active;
  }

  getMeasurements(): ReadonlyArray<PlotexMeasurement> {
    // Return copies of the Vector3s so caller-side mutation can't corrupt
    // the record. Cheap — we only have a handful of measurements at most.
    return this.records.map((r) => ({
      p1: r.p1.clone(),
      p2: r.p2.clone(),
      distance: r.distance,
    }));
  }

  /**
   * Called once per frame from the engine animate loop. Projects each
   * measurement's midpoint through the current camera to keep HTML labels
   * anchored to their world-space midpoints as the user orbits.
   *
   * Keeping the projection work here (not in pointermove) means labels
   * track correctly even when the tool is inactive but records exist.
   */
  update(): void {
    // Reposition persistent labels.
    for (const r of this.records) {
      this._positionLabel(r.labelEl, this._midpoint(r.p1, r.p2));
    }
    // Reposition preview label if we're mid-measurement.
    if (this.state === 'waitingForSecond' && this.pendingP1 && this.previewLabel.style.display !== 'none') {
      // Preview label tracks midpoint between p1 and the current preview
      // line tail, which lives in the geometry attribute we update in
      // _onPointerMove.
      const linePos = this.previewLine.geometry.attributes.position;
      if (linePos) {
        const arr = linePos.array as ArrayLike<number>;
        const p2 = this.midpointScratch.set(arr[3], arr[4], arr[5]);
        const mid = this._midpoint(this.pendingP1, p2);
        this._positionLabel(this.previewLabel, mid);
      }
    }
  }

  // -- Event handlers -------------------------------------------------------

  private _onPointerDown(e: PointerEvent): void {
    // Only primary button places points — secondary/middle should still let
    // OrbitControls pan/orbit as usual.
    if (e.button !== 0) return;

    this._updatePointerNdc(e);
    const hit = this._raycast();
    // Miss? Ignore the click — requiring a valid hit for placement keeps
    // measurements anchored to real geometry rather than arbitrary sky
    // points. The preview line still gave feedback that the tool is live.
    if (!hit) return;

    if (this.state === 'idle') {
      this.pendingP1 = hit.clone();
      this.state = 'waitingForSecond';
      // Show the preview marker at p1 right away. Line + label remain
      // hidden until the first pointermove samples a p2 candidate.
      this.previewMarker.position.copy(hit);
      this.previewMarker.visible = true;
      this.previewLine.visible = false;
      this.previewLabel.style.display = 'none';
    } else {
      // Second click — finalize the measurement.
      if (!this.pendingP1) return;
      this._finalizeMeasurement(this.pendingP1.clone(), hit.clone());
      this.pendingP1 = null;
      this.state = 'idle';
      this._hidePreview();
    }
  }

  private _onPointerMove(e: PointerEvent): void {
    if (this.state !== 'waitingForSecond' || !this.pendingP1) return;

    this._updatePointerNdc(e);
    // Preview wants a point even when we miss — otherwise the line vanishes
    // mid-drag and the user thinks the tool is broken. Fall back to a point
    // NO_HIT_CLAMP_METERS along the ray.
    const hit = this._raycast() ?? this._rayPointAt(NO_HIT_CLAMP_METERS);

    // Update preview line endpoints in-place. Writing the same BufferAttribute
    // each frame avoids geometry churn; setting needsUpdate tells three.js
    // to reupload the VBO next render.
    const posAttr = this.previewLine.geometry.attributes.position as BufferAttribute;
    const p1 = this.pendingP1;
    posAttr.setXYZ(0, p1.x, p1.y, p1.z);
    posAttr.setXYZ(1, hit.x, hit.y, hit.z);
    posAttr.needsUpdate = true;
    this.previewLine.visible = true;

    // Preview label with live distance. Hidden when still near p1 — showing
    // "0.00 m" before the user has meaningfully moved is noise.
    const d = p1.distanceTo(hit);
    this.previewLabel.textContent = this._formatDistance(d);
    this.previewLabel.style.display = d > 0.05 ? 'block' : 'none';
  }

  private _onKeyDown(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    // Mirrors CAD behavior: first Escape cancels the in-progress
    // measurement, second Escape exits the tool entirely.
    if (this.state === 'waitingForSecond') {
      this.pendingP1 = null;
      this.state = 'idle';
      this._hidePreview();
    } else {
      this.deactivate();
    }
  }

  // -- Raycasting helpers ---------------------------------------------------

  private _updatePointerNdc(e: PointerEvent): void {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointerNdc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointerNdc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
  }

  private _raycast(): Vector3 | null {
    this.raycaster.setFromCamera(this.pointerNdc, this.camera);
    const intersects = this.raycaster.intersectObject(this.targetMeshes, true);
    // Filter to visible real meshes only. Helpers, grid, impostor quads
    // (when hidden), and our own markers / lines all get skipped. Our
    // preview + record objects have renderOrder=999 and depthTest=false
    // but they ALSO live in the scene, so without the filter we'd
    // re-intersect our own markers on subsequent clicks.
    for (const hit of intersects) {
      const obj = hit.object as Mesh;
      if (!obj.isMesh || !obj.visible || !obj.material) continue;
      // Skip our own preview / marker meshes explicitly — they're parented
      // to `scene` but shouldn't be raycast targets.
      if (obj === this.previewMarker) continue;
      if (this._isOwnedMarker(obj)) continue;
      return hit.point;
    }
    return null;
  }

  private _isOwnedMarker(obj: Object3D): boolean {
    // Linear scan across records. Records only grow to a few dozen in
    // typical arch-viz sessions, so this is faster than maintaining a Set.
    for (const r of this.records) {
      if (obj === r.marker1 || obj === r.marker2) return true;
    }
    return false;
  }

  /**
   * Returns a point `t` meters along the camera ray for the current pointer
   * NDC. Used when the raycast misses — gives the preview line SOMETHING to
   * anchor to so the user sees feedback that the tool is live.
   */
  private _rayPointAt(t: number): Vector3 {
    this.raycaster.setFromCamera(this.pointerNdc, this.camera);
    const origin = this.raycaster.ray.origin;
    const dir = this.raycaster.ray.direction;
    // new Vector3 here is one allocation per no-hit frame — acceptable
    // because the no-hit path is rare and short-lived.
    return new Vector3(
      origin.x + dir.x * t,
      origin.y + dir.y * t,
      origin.z + dir.z * t,
    );
  }

  // -- Measurement lifecycle ------------------------------------------------

  private _finalizeMeasurement(p1: Vector3, p2: Vector3): void {
    const distance = p1.distanceTo(p2);

    const marker1 = this._buildMarker();
    marker1.position.copy(p1);
    this.scene.add(marker1);

    const marker2 = this._buildMarker();
    marker2.position.copy(p2);
    this.scene.add(marker2);

    const line = this._buildLine();
    const posAttr = line.geometry.attributes.position as BufferAttribute;
    posAttr.setXYZ(0, p1.x, p1.y, p1.z);
    posAttr.setXYZ(1, p2.x, p2.y, p2.z);
    posAttr.needsUpdate = true;
    this.scene.add(line);

    const labelEl = this._buildLabel();
    labelEl.textContent = this._formatDistance(distance);
    this.labelRoot.appendChild(labelEl);

    this.records.push({ p1, p2, distance, marker1, marker2, line, labelEl });
  }

  private _disposeRecord(r: Record): void {
    this.scene.remove(r.marker1);
    (r.marker1.geometry as BufferGeometry).dispose();
    (r.marker1.material as MeshBasicMaterial).dispose();
    this.scene.remove(r.marker2);
    (r.marker2.geometry as BufferGeometry).dispose();
    (r.marker2.material as MeshBasicMaterial).dispose();
    this.scene.remove(r.line);
    (r.line.geometry as BufferGeometry).dispose();
    (r.line.material as LineBasicMaterial).dispose();
    r.labelEl.remove();
  }

  private _hidePreview(): void {
    this.previewMarker.visible = false;
    this.previewLine.visible = false;
    this.previewLabel.style.display = 'none';
  }

  // -- Scene object builders ------------------------------------------------

  private _buildMarker(): Mesh {
    const geom = new SphereGeometry(MARKER_RADIUS, 12, 8);
    const mat = new MeshBasicMaterial({
      color: MARKER_COLOR,
      depthTest: false,
      // Transparent=true combined with depthWrite=false keeps the marker
      // from polluting the depth buffer — important because other render
      // order 999 objects would otherwise compete.
      transparent: true,
      depthWrite: false,
    });
    const m = new Mesh(geom, mat);
    m.renderOrder = 999;
    m.frustumCulled = false;
    return m;
  }

  private _buildLine(): LineSegments {
    // Two-endpoint LineSegments. We pre-allocate the BufferAttribute with
    // six floats (two XYZ points) and rewrite them in place on every move
    // rather than rebuilding the geometry.
    const geom = new BufferGeometry();
    const positions = new Float32Array(6);
    geom.setAttribute('position', new BufferAttribute(positions, 3));
    const mat = new LineBasicMaterial({
      color: LINE_COLOR,
      depthTest: false,
      transparent: true,
      depthWrite: false,
      // linewidth has no effect on WebGL1 outside Chrome, but keeping it
      // set preserves the authoring intent and matches the spec.
      linewidth: 2,
    });
    const line = new LineSegments(geom, mat);
    line.renderOrder = 999;
    line.frustumCulled = false;
    return line;
  }

  private _buildLabel(): HTMLDivElement {
    const el = document.createElement('div');
    Object.assign(el.style, {
      position: 'absolute',
      transform: 'translate(-50%, -50%)',
      padding: '3px 8px',
      background: 'rgba(0, 0, 0, 0.75)',
      color: '#fff',
      fontSize: '11px',
      fontWeight: '600',
      letterSpacing: '0.3px',
      borderRadius: '4px',
      whiteSpace: 'nowrap',
      border: '1px solid rgba(74, 158, 255, 0.5)',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.4)',
      pointerEvents: 'none',
      userSelect: 'none',
    });
    return el;
  }

  // -- Label projection -----------------------------------------------------

  private _midpoint(a: Vector3, b: Vector3): Vector3 {
    // Writes into `midpointScratch` and returns it. Caller must not hold
    // the reference across another call into this method.
    return this.midpointScratch.set(
      (a.x + b.x) * 0.5,
      (a.y + b.y) * 0.5,
      (a.z + b.z) * 0.5,
    );
  }

  private _positionLabel(el: HTMLDivElement, worldPoint: Vector3): void {
    // camera.project() writes into the input vector — copy to scratch
    // first so we don't mutate the caller's midpoint accidentally.
    this.ndcScratch.copy(worldPoint).project(this.camera);

    // Behind the camera: ndc.z > 1 means the point is past the far plane,
    // ndc.z < -1 means behind the near plane. Hide the label either way
    // so it doesn't flip to the opposite side of the screen.
    if (this.ndcScratch.z < -1 || this.ndcScratch.z > 1) {
      el.style.display = 'none';
      return;
    }

    const rect = this.renderer.domElement.getBoundingClientRect();
    const x = rect.left + ((this.ndcScratch.x + 1) * 0.5) * rect.width;
    const y = rect.top + ((1 - this.ndcScratch.y) * 0.5) * rect.height;

    el.style.display = 'block';
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
  }

  private _formatDistance(meters: number): string {
    // Two decimals is a sensible default for arch-viz. Anything finer
    // exceeds the precision most reference scenes actually have.
    return `${meters.toFixed(2)} m`;
  }

  // -- Toolbar button -------------------------------------------------------

  private _buildToolbarButton(): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.textContent = 'Measure';
    btn.setAttribute('aria-pressed', 'false');
    btn.title = 'Measure distance (click two points)';
    Object.assign(btn.style, {
      position: 'fixed',
      // Quality slider sits at bottom:16px; stack this button above it with
      // a 16 px gap. Slider height ~24 px means bottom:56 px clears it with
      // breathing room, but 80 px matches the spec and leaves comfortable
      // whitespace for future widgets to slot between.
      bottom: '80px',
      right: '16px',
      zIndex: '9999',
      width: '40px',
      height: '40px',
      padding: '0',
      borderRadius: '8px',
      border: 'none',
      outline: 'none',
      cursor: 'pointer',
      background: 'rgba(0, 0, 0, 0.55)',
      color: '#ccc',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '10px',
      fontWeight: '500',
      letterSpacing: '0.2px',
      transition: 'background 0.15s ease, color 0.15s ease',
      userSelect: 'none',
    });
    btn.addEventListener('click', () => {
      if (this.active) this.deactivate();
      else this.activate();
    });
    // Subtle hover pop so the button reads as interactive at the muted
    // opacity we're using for chrome.
    btn.addEventListener('mouseenter', () => {
      if (!this.active) btn.style.background = 'rgba(255, 255, 255, 0.15)';
    });
    btn.addEventListener('mouseleave', () => {
      if (!this.active) btn.style.background = 'rgba(0, 0, 0, 0.55)';
    });
    return btn;
  }

  private _setButtonActive(active: boolean): void {
    this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
    if (active) {
      this.button.style.background = '#3b82f6';
      this.button.style.color = '#fff';
    } else {
      this.button.style.background = 'rgba(0, 0, 0, 0.55)';
      this.button.style.color = '#ccc';
    }
  }
}
